import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { filter, firstValueFrom } from 'rxjs';
import {
    shouldRedirectToSchedulesUpsell,
    shouldRedirectToWebAppUsageUpsell
} from 'src/app/constants/upsell-setup-flow.constants';
import { NewBillingState } from 'src/app/pages/new-billing/store/state';
import { UPSELL_REGISTRY } from 'src/app/pages/upgrade/upsell-registry';
import { DemoService } from 'src/app/services/demo/demo.service';
import { shouldBypassReportUpsellWhenDemoData } from 'src/app/services/demo/demo-mock-data-visibility';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { SelfServeActivationService } from 'src/app/services/self-serve-activation/self-serve-activation.service';
import { AuthState } from 'src/app/store/auth/auth.state';

/**
 * Functional guard factory that redirects to /upgrade/:upsellName
 * when the upsell feature flag is enabled and the addon is not purchased.
 *
 * Usage in routing:
 *   canActivate: [PaidProtectedGuard, upsellRedirectGuard('benchmarks')]
 */
export function upsellRedirectGuard(upsellName: string): CanActivateFn {
  return async (_route, _state) => {
    const growthbook = inject(GrowthBookService);
    const selfServe = inject(SelfServeActivationService);
    const store = inject(Store);
    const router = inject(Router);
    const demoService = inject(DemoService);

    // Wait for GrowthBook to be initialized
    await firstValueFrom(growthbook.initialized.pipe(filter(x => x)));

    const config = UPSELL_REGISTRY.get(upsellName);
    if (!config) {
      return true;
    }

    if (upsellName === 'benchmarks') {
      const showDemoData = await firstValueFrom(
        demoService.showBenchmarksDemoData$.pipe(filter((value): value is boolean => value !== undefined)),
      );
      if (shouldBypassReportUpsellWhenDemoData(showDemoData)) {
        return true;
      }
    }

    const company = store.selectSnapshot(AuthState.company);
    const rawValue = growthbook.getFeatureValue(config.featureFlagKey, null);
    const flagEnabled = upsellName === 'schedules'
      ? shouldRedirectToSchedulesUpsell(growthbook, company)
      : upsellName === 'web-app-usage'
        ? shouldRedirectToWebAppUsageUpsell(growthbook, company)
        : config.featureFlagEnabled ? config.featureFlagEnabled(rawValue) : !!rawValue;
    if (!flagEnabled) {
      return true;
    }

    // Check if addon is already purchased (via addon linkname, normalized lookup, or legacy keys)
    if (config.addonLinkname || config.addonLinknameNormalized || config.legacySubscriptionKeys?.length) {
      const billingData = store.selectSnapshot(NewBillingState.billingData);
      const ids = config.addonLinkname
        ? selfServe.readAddonFromGB(config.addonLinkname).ids
        : config.addonLinknameNormalized
          ? selfServe.readAddonFromGBNormalized(config.addonLinknameNormalized).ids
          : null;
      const purchased = selfServe.hasPurchasedAddon(
        company,
        billingData,
        ids,
        config.legacySubscriptionKeys,
        config.additionalBillingPlanIdsForAddonMatch,
      );

      if (purchased) {
        return true;
      }
    }

    // Redirect to upsell page
    return router.createUrlTree(['/upgrade', config.name]);
  };
}
